import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';
import { VmAuthContextResponse } from './import.models';

@Component({
  selector: 'app-import-vm-auth',
  imports: [FormsModule],
  template: `
    <section class="card">
      <h1>Sign In to Visual Matrix</h1>
      <p class="product">Visual Matrix Importer</p>
      <p class="hint">{{ contextHint() }}</p>

      @if (message()) {
        <div class="msg" [class.err]="isError()">{{ message() }}</div>
      }

      <form class="form" (submit)="submit($event)">
        <label>
          <span>VM Property ID</span>
          <input [(ngModel)]="propertyId" name="propertyId" type="text" required readonly>
        </label>
        <label>
          <span>VM Username</span>
          <input [(ngModel)]="username" name="username" type="text" required>
        </label>
        <label>
          <span>VM Password</span>
          <input [(ngModel)]="password" name="password" type="password" required>
        </label>
        <div class="actions">
          <button type="submit" [disabled]="busy()">{{ busy() ? 'Signing in…' : submitLabel() }}</button>
        </div>
      </form>
    </section>
  `,
  styles: `
    .card{max-width:760px;margin:0 auto;background:#fff;border:1px solid var(--line);border-radius:14px;padding:18px;display:grid;gap:14px}
    h1{margin:0}
    .product{margin:0;color:#24334a;font-weight:700;font-size:14px;letter-spacing:.02em}
    .hint{margin:0;color:var(--muted)}
    .form{display:grid;gap:14px}
    label{display:grid;gap:6px;font-weight:600;color:#24334a}
    input{height:42px;border:1px solid #c8d2e1;border-radius:10px;padding:0 12px}
    .actions{display:flex;gap:10px;flex-wrap:wrap}
    button{height:40px;border:0;border-radius:10px;background:var(--primary);color:#fff;font-weight:700;padding:0 14px;cursor:pointer}
    .msg{padding:10px 12px;border-radius:10px;background:#eef4ff;color:#0a2f6b;border:1px solid #c7d7f5}
    .msg.err{background:#fdecec;color:#8a1f1f;border-color:#f2c4c4}
  `,
})
export class ImportVmAuthComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly busy = signal(false);
  readonly message = signal('');
  readonly isError = signal(false);
  readonly submitLabel = signal('Continue');
  readonly contextHint = signal('Direct API actions require an active Visual Matrix session.');

  next: 'map' | 'bulk' | 'reservations' | 'catalog' = 'map';
  baseUrl = 'https://login.vmcloudpms.com';
  propertyId = '';
  username = '';
  password = '';

  async ngOnInit(): Promise<void> {
    this.next = (this.route.snapshot.queryParamMap.get('next') as 'map' | 'bulk' | 'reservations' | 'catalog' | null) ?? 'map';
    await this.seedFromActivePropertyContext();
    await this.loadContext();
  }

  private async seedFromActivePropertyContext(): Promise<void> {
    try {
      const active = await firstValueFrom(
        this.api.get<{ activeProperty: { code: string; vmApiBaseUrl?: string } | null }>('/context/property'),
      );
      const code = String(active.activeProperty?.code || '').trim();
      const baseUrl = String(active.activeProperty?.vmApiBaseUrl || '').trim();
      if (code) this.propertyId = code;
      if (baseUrl) this.baseUrl = baseUrl;
    } catch {
      // no-op; loadContext handles final redirects/errors
    }
  }

  private async loadContext(): Promise<void> {
    this.busy.set(true);
    try {
      const ctx = await firstValueFrom(this.api.get<VmAuthContextResponse>(`/import/vm-auth-context?next=${this.next}`));
      if (!ctx.ok) {
        await this.router.navigateByUrl(ctx.redirectTo || '/import/upload');
        return;
      }
      this.submitLabel.set(ctx.submitLabel);
      this.contextHint.set(ctx.contextHint.replace(/Use existing login\.?\s*/gi, '').trim());
      this.baseUrl = String(ctx.vmBaseUrl || '').trim();
      this.propertyId = String(ctx.expectedPropertyId || '').trim();
      if (!this.propertyId) {
        const fallback = await firstValueFrom(
          this.api.get<{ activeProperty: { code: string; vmApiBaseUrl?: string } | null }>('/context/property'),
        );
        this.propertyId = String(fallback.activeProperty?.code || '').trim();
        if (!this.baseUrl) {
          this.baseUrl = String(fallback.activeProperty?.vmApiBaseUrl || 'https://login.vmcloudpms.com').trim();
        }
      }
      if (!this.baseUrl) {
        this.baseUrl = 'https://login.vmcloudpms.com';
      }
      if (!this.propertyId) {
        this.isError.set(true);
        this.message.set('Unable to determine VM Property ID from active property context. Re-select property and try again.');
        await this.router.navigateByUrl('/properties');
        return;
      }
      if (ctx.message) {
        this.isError.set(true);
        this.message.set(ctx.message);
      }
      if (!ctx.needLogin && ctx.redirectTo) {
        window.dispatchEvent(new Event('vm-session-changed'));
        await this.router.navigateByUrl(ctx.redirectTo);
      }
    } catch {
      this.isError.set(true);
      this.message.set('Failed to load Visual Matrix login context.');
    } finally {
      this.busy.set(false);
    }
  }

  async submit(event: Event): Promise<void> {
    event.preventDefault();
    this.busy.set(true);
    this.isError.set(false);
    this.message.set('');
    try {
      const res = await firstValueFrom(
        this.api.post<{ ok: boolean; error?: string }>('/vm/login', {
          baseUrl: this.baseUrl,
          propertyId: this.propertyId.trim(),
          username: this.username.trim(),
          password: this.password,
        }),
      );
      if (!res.ok) {
        this.isError.set(true);
        this.message.set(res.error || 'VM login failed.');
        return;
      }
      this.password = '';
      window.dispatchEvent(new Event('vm-session-changed'));
      await this.router.navigateByUrl(
        this.next === 'map'
          ? '/import/map'
          : this.next === 'bulk'
            ? '/reservations/bulk'
            : this.next === 'catalog'
              ? '/catalog?autocheck=1'
              : '/reservations',
      );
    } catch {
      this.isError.set(true);
      this.message.set('VM login failed.');
    } finally {
      this.busy.set(false);
    }
  }
}
